/**
 * Amplitude BrowserPlugin Helper Functions
 *
 * 브라우저, 디바이스, 플랫폼 정보를 추출하는 헬퍼 함수 모음
 */

import { UAParser } from 'ua-parser-js'

import { getCountryFromUrl } from '../language'

import { detectWebViewFromUserAgent } from '@/shared/utils/webview-utils'

import type { DeviceType, PageDomain, PageType, PlatformDetail, PlatformType } from './browser-plugin.types'

/**
 * Platform Type 감지
 * WebView, Web, In-App Browser 구분
 */
export const getPlatformType = (): PlatformType => {
  if (typeof window === 'undefined') return 'web'

  const userAgent = navigator.userAgent
  const detection = detectWebViewFromUserAgent(userAgent)

  // WebView 감지
  if (detection.looksLikeAndroidWebView || detection.looksLikeIOSWebView) {
    return 'webview'
  }

  // In-App Browser 감지 (Facebook, Instagram, KakaoTalk 등)
  if (detection.isInAppBrowserUA) {
    return 'in_app_browser'
  }

  return 'web'
}

/**
 * Platform 상세 정보
 */
export const getPlatformDetail = (): PlatformDetail => {
  if (typeof window === 'undefined') return 'other'

  const ua = navigator.userAgent.toLowerCase()
  const detection = detectWebViewFromUserAgent(navigator.userAgent)

  // WebView 감지
  if (detection.looksLikeIOSWebView) {
    return 'ios_webview'
  }
  if (detection.looksLikeAndroidWebView) {
    return 'android_webview'
  }
  if (typeof (window as any).ReactNativeWebView !== 'undefined') {
    return 'react_native_webview'
  }

  // In-App Browser 감지
  if (ua.includes('fban') || ua.includes('fbav')) return 'facebook'
  if (ua.includes('instagram')) return 'instagram'
  if (ua.includes('kakaotalk')) return 'kakaotalk'
  if (ua.includes('naver')) return 'naver'
  if (ua.includes(' line/')) return 'line'
  if (ua.includes(' gsa/')) return 'google_app'

  // 일반 브라우저
  if (ua.includes('safari') && !ua.includes('chrome')) return 'safari'
  if (ua.includes('chrome') && !ua.includes('edg')) return 'chrome'
  if (ua.includes('firefox')) return 'firefox'
  if (ua.includes('edg')) return 'edge'
  if (ua.includes('samsungbrowser')) return 'samsung_internet'
  if (ua.includes('opr') || ua.includes('opera')) return 'opera'

  return 'other'
}

/**
 * Page Domain 분류
 * URL path를 기반으로 페이지 도메인 추출
 */
export const getPageDomain = (pathname: string): PageDomain => {
  // Locale 제거
  const pathWithoutLocale = getPathWithoutLocale(pathname)
  const segments = pathWithoutLocale.split('/').filter(Boolean)

  if (segments.length === 0) return 'home'
  if (pathWithoutLocale.includes('/medical-tips')) return 'medical_tip'
  if (pathWithoutLocale.includes('/reviews')) return 'review'

  const firstSegment = segments[0]

  // 페이지 도메인 매핑
  const domainMap: Record<string, PageDomain> = {
    doctors: 'doctor',
    hospitals: 'hospital',
    events: 'service_offer',
    posts: 'column',
    community: 'community',
    reviews: 'review',
    search: 'search',
    chat: 'chat',
    chats: 'chat',
    mypage: 'mypage',
    signin: 'signin',
    signup: 'signup',
    info: 'info',
    about: 'about',
    'about-unni': 'about',
    agreements: 'agreement',
    'district-filter': 'district_filter',
    download: 'download',
    'unni-guide': 'unni_guide',
    'unni-guide-v2': 'unni_guide',
  }

  return domainMap[firstSegment] || 'other'
}

/**
 * Page Type 분류
 * URL path를 기반으로 페이지 타입 추출
 */
export const getPageType = (pathname: string): PageType => {
  const pathWithoutLocale = getPathWithoutLocale(pathname)
  const segments = pathWithoutLocale.split('/').filter(Boolean)

  // 홈 페이지
  if (segments.length === 0) return 'index'

  // 특정 키워드 기반 세밀한 분류 (depth보다 우선)
  if (pathWithoutLocale.includes('/oauth')) return 'auth'
  if (pathWithoutLocale.endsWith('/complete')) return 'completion'
  if (pathWithoutLocale.includes('/inquiry')) return 'inquiry'
  if (pathWithoutLocale.includes('/reservation') || pathWithoutLocale.includes('/callback-reservations'))
    return 'reservation'
  if (pathWithoutLocale.includes('/consultation')) return 'consultation'

  // 검색 페이지
  if (segments[0] === 'search') return 'search_result'

  // List vs Detail 구분
  // /doctors, /hospitals 등 → list
  // /doctors/123, /hospitals/456 등 → detail
  if (segments.length === 1) {
    // /signin, /signup 등은 form
    if (['signin', 'signup'].includes(segments[0])) return 'form'
    return 'list'
  }

  // 2개 이상 세그먼트
  if (segments.length >= 2) {
    const secondSegment = segments[1]

    // /hospitals/[id]/reviews 같은 경우
    if (segments.length >= 3) return 'detail'

    // 숫자 ID면 detail, 아니면 list/form 판단
    if (/^\d+$/.test(secondSegment)) return 'detail'

    return 'list'
  }

  return 'other'
}

/**
 * Locale 제거한 Path 반환
 * /jp/doctors/123 → /doctors/123
 */
export const getPathWithoutLocale = (pathname: string): string => {
  const country = getCountryFromUrl(pathname)

  // KR이면 locale prefix 없음
  if (country === 'kr') return pathname

  // Locale prefix 제거
  const localePatterns = ['/jp', '/us', '/th', '/zh-hans', '/zh-hant', '/zh-CN']

  for (const pattern of localePatterns) {
    if (pathname.startsWith(pattern)) {
      return pathname.slice(pattern.length) || '/'
    }
  }

  return pathname
}

/**
 * Path Segments 배열 반환
 * /doctors/123 → ['doctors', '123']
 */
export const getPathSegments = (pathname: string): string[] => {
  const pathWithoutLocale = getPathWithoutLocale(pathname)
  return pathWithoutLocale.split('/').filter(Boolean)
}

/**
 * Device Type 감지
 * Mobile, Tablet, Desktop 구분
 */
export const getDeviceType = (): DeviceType => {
  if (typeof window === 'undefined') return 'desktop'

  const ua = navigator.userAgent.toLowerCase()

  // Tablet 감지
  if (ua.includes('ipad') || (ua.includes('android') && !ua.includes('mobile'))) {
    return 'tablet'
  }

  // Mobile 감지
  if (
    ua.includes('iphone') ||
    ua.includes('ipod') ||
    ua.includes('android') ||
    ua.includes('mobile') ||
    ua.includes('blackberry') ||
    ua.includes('opera mini')
  ) {
    return 'mobile'
  }

  return 'desktop'
}

/**
 * Mobile 여부
 */
export const isMobile = (): boolean => {
  return getDeviceType() === 'mobile'
}

/**
 * Browser 정보 파싱 (UA Parser 사용)
 */
export const getBrowserInfo = () => {
  if (typeof window === 'undefined') {
    return {
      browser_name: null,
      browser_version: null,
      os_name: null,
      os_version: null,
    }
  }

  const parser = new UAParser(navigator.userAgent)
  const browser = parser.getBrowser()
  const os = parser.getOS()

  return {
    browser_name: browser.name?.toLowerCase() || null,
    browser_version: browser.version || null,
    os_name: os.name || null,
    os_version: os.version || null,
  }
}

/**
 * Referring Domain 추출
 */
export const getReferringDomain = (): string | null => {
  if (typeof document === 'undefined' || !document.referrer) return null

  try {
    return new URL(document.referrer).hostname
  } catch {
    return null
  }
}
